import { useQuery } from '@apollo/client'
import { PARTNER_CLIENTS_BY_PERSON_ID } from './partner-clients'
import type { PartnerClient, PartnerClientsByPersonIdInput, PartnerClientsResponse } from './partner-clients'

const PROXY_CONTEXT = { clientName: 'proxy' as const }

type PartnerClientsOptions = {
  context?: { clientName: 'proxy' | 'direct' }
}

const toPartnerClientsVariables = (input: PartnerClientsByPersonIdInput) => ({
  input: {
    person_id: input.personId,
    context: input.context,
    status_filters: input.statusFilters,
  },
})

/**
 * Hook to look up the clients (leads) associated with a person for a partner context.
 */
export const usePartnerClientsByPersonId = (
  input: PartnerClientsByPersonIdInput | null,
  options?: PartnerClientsOptions
) => {
  const query = useQuery<PartnerClientsResponse>(PARTNER_CLIENTS_BY_PERSON_ID, {
    variables: input ? toPartnerClientsVariables(input) : undefined,
    context: options?.context ?? PROXY_CONTEXT,
    fetchPolicy: 'cache-and-network',
    skip: !input,
  })

  const clients: PartnerClient[] = (query.data?.partnerClientsByPersonId ?? []).map((client) => ({
    leadId: client.leadId,
    status: client.status,
    borrowerName: client.borrower?.name ?? null,
    departingPropertyAddress1: client.departingPropertyAddress1,
    departingPropertyAddress2: client.departingPropertyAddress2,
    transactionId: client.transactionId,
    transactionType: client.transactionType,
  }))

  return {
    clients,
    loading: query.loading,
    error: query.error,
    refetch: query.refetch,
  }
}
